import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, Download, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtInr, fmtDate, API } from "../lib/api";

export default function BillingStatementPage() {
    const { siteId } = useParams();
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [st, setSt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/sites/${siteId}/billing-statement`, { params: { month } });
                setSt(data);
            } catch (e) { toast.error(apiError(e)); }
            finally { setLoading(false); }
        })();
    }, [siteId, month]);

    async function toggleReimbursement(expenseId, curr) {
        try {
            await api.patch(`/expenses/${expenseId}/reimbursement`, { separately_reimbursed: !curr });
            const { data } = await api.get(`/sites/${siteId}/billing-statement`, { params: { month } });
            setSt(data);
            toast.success("Updated");
        } catch (e) { toast.error(apiError(e)); }
    }

    async function saveConfig(patch) {
        try {
            await api.patch(`/sites/${siteId}/billing-config`, patch);
            const { data } = await api.get(`/sites/${siteId}/billing-statement`, { params: { month } });
            setSt(data);
            toast.success("Billing config saved");
        } catch (e) { toast.error(apiError(e)); }
    }

    if (loading) return <div className="mx-auto max-w-5xl p-6"><div className="h-96 rounded-2xl bg-white animate-pulse" /></div>;
    if (!st) return null;

    const token = localStorage.getItem("fx_token");
    const download = (fmt) => {
        const u = `${API}/sites/${siteId}/billing-statement/export.${fmt}?month=${month}`;
        // Fetch as blob so we can attach the auth header
        fetch(u, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(b);
                link.download = `billing-${st.site_name}-${month}.${fmt}`;
                link.click();
            })
            .catch(() => toast.error("Download failed"));
    };

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8" data-testid="billing-statement-page">
            <Link to={`/sites/${siteId}/expenses`} className="inline-flex items-center gap-1 text-xs text-[#1E3355] hover:underline mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to expenses
            </Link>
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Monthly site-wise expense report</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                        <FileText className="w-6 h-6" /> {st.client_legal_name}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {month} · Prepared by Flexworx Ops · Cost + <strong>{st.billing_margin_pct}%</strong> handling
                        {st.billing_gst_pct > 0 && <> + <strong>GST @{st.billing_gst_pct}%</strong></>}
                    </p>
                </div>
                <div className="flex items-end gap-2">
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white" data-testid="billing-month" />
                    <button onClick={() => download("csv")} className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 text-sm text-[#1E3355]" data-testid="download-csv">
                        <Download className="w-4 h-4" /> CSV
                    </button>
                    <button onClick={() => download("pdf")} className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white px-3 py-1.5 text-sm" data-testid="download-pdf">
                        <Download className="w-4 h-4" /> PDF
                    </button>
                    <button
                        onClick={async () => {
                            if (!window.confirm(`Send the ${month} statement to the client's billing contact and file the invoice ledger row?`)) return;
                            setSending(true);
                            try {
                                const { data } = await api.post(`/billing/${siteId}/${month}/send`);
                                toast.success(`Statement sent · ₹${Math.round(data.amount_inr).toLocaleString("en-IN")} · ${(data.recipients || []).join(", ") || "queued"}`);
                            } catch (e) { toast.error(apiError(e)); }
                            finally { setSending(false); }
                        }}
                        disabled={sending}
                        className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-sm disabled:opacity-60"
                        data-testid="bill-this-month"
                    >
                        <Send className="w-4 h-4" /> {sending ? "Sending…" : "Bill this month"}
                    </button>
                </div>
            </div>

            {/* Billing config */}
            <div className="mt-4 fx-card p-3 flex flex-wrap gap-3 items-center text-xs">
                <span className="text-gray-500 font-semibold uppercase tracking-widest">Billing config</span>
                <label className="flex items-center gap-1">Margin %
                    <input type="number" step="0.5" defaultValue={st.billing_margin_pct} onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (Number.isFinite(v) && v !== st.billing_margin_pct) saveConfig({ billing_margin_pct: v });
                    }} className="w-16 rounded border border-gray-300 px-1 py-0.5" data-testid="cfg-margin" />
                </label>
                <label className="flex items-center gap-1">GST %
                    <input type="number" step="0.5" defaultValue={st.billing_gst_pct} onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (Number.isFinite(v) && v !== st.billing_gst_pct) saveConfig({ billing_gst_pct: v });
                    }} className="w-16 rounded border border-gray-300 px-1 py-0.5" data-testid="cfg-gst" />
                </label>
                <span className="text-gray-400 ml-auto">Set GST to 0 to disable.</span>
            </div>

            <div className="mt-5 fx-card overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead className="bg-[#1E3355] text-white">
                        <tr>
                            <th className="p-2 text-left">Sl</th>
                            <th className="p-2 text-left">Vendor</th>
                            <th className="p-2 text-left">Invoice No</th>
                            <th className="p-2 text-left">Date</th>
                            <th className="p-2 text-right">Maintenance</th>
                            <th className="p-2 text-right">Electricity</th>
                            <th className="p-2 text-right">Salary</th>
                            <th className="p-2 text-right">Total</th>
                            <th className="p-2 text-left">Remarks</th>
                            <th className="p-2 text-center">Reimbursed*</th>
                        </tr>
                    </thead>
                    <tbody>
                        {st.lines.map((l) => (
                            <tr key={l.expense_id} className="border-t border-gray-100" data-testid={`billing-line-${l.sl_no}`}>
                                <td className="p-2">{l.sl_no}</td>
                                <td className="p-2 font-medium">{l.vendor_name}</td>
                                <td className="p-2 text-gray-500">{l.invoice_number}</td>
                                <td className="p-2 text-gray-500">{l.invoice_date}</td>
                                <td className="p-2 text-right">{l.maintenance ? fmtInr(l.maintenance) : ""}</td>
                                <td className="p-2 text-right">{l.electricity ? fmtInr(l.electricity) : ""}</td>
                                <td className="p-2 text-right">{l.salary ? fmtInr(l.salary) : ""}</td>
                                <td className="p-2 text-right font-semibold text-[#1E3355]">{fmtInr(l.total)}</td>
                                <td className="p-2 text-gray-500">{l.remarks}</td>
                                <td className="p-2 text-center">
                                    <input
                                        type="checkbox"
                                        checked={!!l.separately_reimbursed}
                                        onChange={() => toggleReimbursement(l.expense_id, l.separately_reimbursed)}
                                        data-testid={`reimb-${l.sl_no}`}
                                    />
                                </td>
                            </tr>
                        ))}
                        <tr className="border-t-2 border-[#1E3355] bg-[#F4F6F9] font-semibold">
                            <td className="p-2" colSpan={4}>SUBTOTALS</td>
                            <td className="p-2 text-right">{fmtInr(st.subtotals.maintenance)}</td>
                            <td className="p-2 text-right">{fmtInr(st.subtotals.electricity)}</td>
                            <td className="p-2 text-right">{fmtInr(st.subtotals.salary)}</td>
                            <td className="p-2 text-right text-[#1E3355]">{fmtInr(st.subtotals.grand)}</td>
                            <td colSpan={2}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2"></div>
                <div className="fx-card p-4 space-y-2 text-sm">
                    <Row label="Grand Total (all expenses)" value={fmtInr(st.subtotals.grand)} />
                    <Row label="Less: separately reimbursed" value={`- ${fmtInr(st.reimbursed_total)}`} muted />
                    <Row label="Billed base" value={fmtInr(st.base)} bold />
                    <Row label={`Add ${st.billing_margin_pct}% handling`} value={fmtInr(st.margin_amount)} />
                    <Row label="Subtotal" value={fmtInr(st.subtotal_after_margin)} bold />
                    {st.billing_gst_pct > 0 && <Row label={`Add GST @${st.billing_gst_pct}%`} value={fmtInr(st.gst_amount)} />}
                    <div className="border-t-2 border-[#1E3355] pt-2">
                        <Row label="GRAND TOTAL BILLED" value={fmtInr(st.grand_total_billed)} big testid="grand-total" />
                    </div>
                </div>
            </div>

            <div className="mt-3 text-[11px] text-gray-400">
                * Separately-reimbursed lines (e.g. electricity paid direct) are excluded from the billed base and receive no margin.
            </div>
        </div>
    );
}

function Row({ label, value, bold, big, muted, testid }) {
    return (
        <div className={`flex justify-between items-baseline ${bold ? "font-semibold" : ""} ${muted ? "text-gray-500" : "text-[#1E3355]"}`}>
            <span className={big ? "text-xs uppercase tracking-widest" : ""}>{label}</span>
            <span className={big ? "font-display text-xl" : "font-mono"} data-testid={testid}>{value}</span>
        </div>
    );
}
